import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../client';

export type OcppDirection = 'in' | 'out';
export type OcppMessageType = 'call' | 'callresult' | 'callerror';

export interface OcppMessage {
  /** Synthetic id, useful as React key. */
  id: string;
  deviceId: string;
  direction: OcppDirection;
  messageType: OcppMessageType;
  uid?: string;
  action?: string;
  payload?: unknown;
  ts: string;
}

interface KeyResponse {
  key: string;
  ttl: number;
}

interface DeviceConsoleState {
  messages: OcppMessage[];
  connected: boolean;
  /** Total count delivered so far (independent of buffer cap). */
  total: number;
}

const DEFAULT_BUFFER = 250;

/**
 * useDeviceConsole opens a /event-hub WS, registers `deviceId` on the
 * server-side watch list, and accumulates OCPP message envelopes for that
 * device into a bounded rolling buffer.
 */
export function useDeviceConsole(
  deviceId: string | undefined,
  options: { enabled?: boolean; bufferSize?: number } = {},
) {
  const enabled = options.enabled !== false && Boolean(deviceId);
  const bufferSize = options.bufferSize ?? DEFAULT_BUFFER;

  const [state, setState] = useState<DeviceConsoleState>({
    messages: [],
    connected: false,
    total: 0,
  });
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);

  const clear = useCallback(() => {
    setState((s) => ({ ...s, messages: [], total: 0 }));
  }, []);

  useEffect(() => {
    if (!enabled || !deviceId) return;
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      try {
        const { key } = await api().post<KeyResponse>('/event-hub');
        if (cancelled) return;
        const baseURL = api().baseURL.replace(/^http/, 'ws');
        const ws = new WebSocket(`${baseURL}/event-hub?key=${encodeURIComponent(key)}`);
        wsRef.current = ws;
        ws.onopen = () => {
          retryRef.current = 0;
          setState((s) => ({ ...s, connected: true }));
          ws.send(JSON.stringify({ list: [deviceId] }));
        };
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data) as Record<string, unknown>;
            if (data.type !== 'ocpp' || data.deviceId !== deviceId) return;
            const msg: OcppMessage = {
              id: `${data.uid ?? Math.random().toString(36).slice(2)}-${Date.now()}-${Math.random()
                .toString(36)
                .slice(2, 6)}`,
              deviceId: String(data.deviceId),
              direction: (data.direction as OcppDirection) ?? 'in',
              messageType: (data.messageType as OcppMessageType) ?? 'call',
              uid: typeof data.uid === 'string' ? data.uid : undefined,
              action: typeof data.action === 'string' ? data.action : undefined,
              payload: data.payload,
              ts: typeof data.ts === 'string' ? data.ts : new Date().toISOString(),
            };
            setState((s) => ({
              ...s,
              messages: [msg, ...s.messages].slice(0, bufferSize),
              total: s.total + 1,
            }));
          } catch {
            /* ignore */
          }
        };
        ws.onclose = () => {
          setState((s) => ({ ...s, connected: false }));
          if (cancelled) return;
          const delay = Math.min(30_000, 1_000 * 2 ** retryRef.current++);
          timeout = setTimeout(connect, delay);
        };
        ws.onerror = () => ws.close();
      } catch {
        if (cancelled) return;
        const delay = Math.min(30_000, 1_000 * 2 ** retryRef.current++);
        timeout = setTimeout(connect, delay);
      }
    };
    void connect();
    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
      wsRef.current?.close();
    };
  }, [enabled, deviceId, bufferSize]);

  return { ...state, clear };
}
